/**
 * Scheduling Integration for Subcontracting
 * Handles workstation availability, timeline adjustments, and capacity planning
 */

import { supabase } from '@/lib/supabase/client';
import { 
  SubcontractDispatchedEvent, 
  SubcontractReceivedEvent, 
  SubcontractClosedEvent,
  subcontractingEvents 
} from '@/lib/events/subcontractingEvents';

export interface WorkstationSchedule {
  id?: string;
  workstation_id: string;
  job_card_id: string;
  operation_id: string;
  scheduled_start: string;
  scheduled_end: string;
  actual_start?: string;
  actual_end?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'outsourced' | 'cancelled';
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CapacityAdjustment {
  workstationId: string;
  workstationName: string;
  freedCapacityHours: number;
  adjustmentDate: string;
  reason: string;
  subContractId: string;
}

class SchedulingIntegration {

  /**
   * Handle subcontract dispatch - Free up workstation capacity
   */
  async handleSubcontractDispatch(event: SubcontractDispatchedEvent): Promise<void> {
    try {
      console.log('📅 Processing scheduling for subcontract dispatch:', event.subContractNumber);

      const capacityAdjustments: CapacityAdjustment[] = [];

      // Update workstation schedules to 'outsourced' status
      for (const operationId of event.data.operationIds) {
        // Get operation details
        const { data: operation, error: opError } = await supabase
          .from('routing_operations')
          .select(`
            id,
            operation_name,
            setup_time,
            cycle_time,
            workstation_id,
            workstations(id, name)
          `)
          .eq('id', operationId)
          .single();

        if (opError || !operation) {
          console.warn(`Operation ${operationId} not found:`, opError);
          continue;
        }

        // Update scheduled entries to outsourced status
        const { error: scheduleError } = await supabase
          .from('workstation_schedules')
          .update({
            status: 'outsourced',
            notes: `Outsourced to ${event.vendorName} - SC: ${event.subContractNumber}`,
            updated_at: event.timestamp
          })
          .eq('operation_id', operationId)
          .in('status', ['scheduled', 'in_progress']);

        if (scheduleError) {
          console.error(`Error updating schedule for operation ${operationId}:`, scheduleError);
          continue;
        }

        // Calculate freed capacity
        const totalTimePerUnit = (operation.setup_time || 0) + (operation.cycle_time || 0);
        const freedCapacityHours = totalTimePerUnit / 60; // Convert minutes to hours

        if (operation.workstations && freedCapacityHours > 0) {
          capacityAdjustments.push({
            workstationId: operation.workstation_id,
            workstationName: operation.workstations.name,
            freedCapacityHours,
            adjustmentDate: event.timestamp,
            reason: `Subcontracted to ${event.vendorName}`,
            subContractId: event.subContractId
          });
        }
      }

      // Log capacity adjustments for workstation planning
      if (capacityAdjustments.length > 0) {
        await this.logCapacityAdjustments(capacityAdjustments);
        console.log(`✅ Freed capacity on ${capacityAdjustments.length} workstations`);
      }

      // Update job card scheduling status
      await this.updateJobCardSchedulingStatus(event.data.jobCardIds, 'outsourced_to_vendor');

    } catch (error) {
      console.error('Error in handleSubcontractDispatch:', error);
      throw error;
    }
  }

  /**
   * Handle subcontract receipt - Update timeline and capacity
   */
  async handleSubcontractReceipt(event: SubcontractReceivedEvent): Promise<void> {
    try {
      console.log('⏰ Processing scheduling for subcontract receipt:', event.subContractNumber);

      // Update job card scheduling status
      await this.updateJobCardSchedulingStatus(event.data.jobCardIds, 'returned_from_vendor');

      // Check if job cards can proceed to next operations
      for (const jobCardId of event.data.jobCardIds) {
        await this.checkAndScheduleNextOperations(jobCardId);
      }

      console.log(`✅ Updated scheduling for ${event.data.jobCardIds.length} job cards`);

    } catch (error) {
      console.error('Error in handleSubcontractReceipt:', error);
      throw error;
    }
  }

  /**
   * Handle subcontract closure - Finalize scheduling adjustments
   */
  async handleSubcontractClosure(event: SubcontractClosedEvent): Promise<void> {
    try {
      console.log('🏁 Processing scheduling for subcontract closure:', event.subContractNumber);

      // Update job card scheduling status to completed
      await this.updateJobCardSchedulingStatus(event.data.jobCardIds, 'subcontract_completed');

      // Archive old schedule entries
      await this.archiveSubcontractSchedules(event.subContractId);

      console.log(`✅ Finalized scheduling for ${event.data.jobCardIds.length} job cards`);

    } catch (error) {
      console.error('Error in handleSubcontractClosure:', error);
      throw error;
    }
  }

  /**
   * Update job card scheduling status
   */
  private async updateJobCardSchedulingStatus(jobCardIds: string[], status: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('job_cards')
        .update({
          scheduling_status: status,
          updated_at: new Date().toISOString()
        })
        .in('id', jobCardIds);

      if (error) {
        console.error('Error updating job card scheduling status:', error);
        throw error;
      }

      console.log(`✅ Updated scheduling status for ${jobCardIds.length} job cards to: ${status}`);
    } catch (error) {
      console.error('Error in updateJobCardSchedulingStatus:', error);
      throw error;
    }
  }

  /**
   * Log capacity adjustments for workstation planning
   */
  private async logCapacityAdjustments(adjustments: CapacityAdjustment[]): Promise<void> {
    try {
      const capacityLogs = adjustments.map(adj => ({
        workstation_id: adj.workstationId,
        adjustment_type: 'capacity_freed',
        adjustment_hours: adj.freedCapacityHours,
        adjustment_date: adj.adjustmentDate,
        reason: adj.reason,
        reference_type: 'sub_contract',
        reference_id: adj.subContractId,
        created_at: adj.adjustmentDate
      }));

      const { error } = await supabase
        .from('workstation_capacity_logs')
        .insert(capacityLogs);

      if (error) {
        console.error('Error logging capacity adjustments:', error);
        // Don't throw - this is just for tracking
      } else {
        console.log(`✅ Logged ${capacityLogs.length} capacity adjustments`);
      }
    } catch (error) {
      console.error('Error in logCapacityAdjustments:', error);
      // Don't throw - this is just for tracking
    }
  }

  /**
   * Check and schedule next operations for a job card
   */
  private async checkAndScheduleNextOperations(jobCardId: string): Promise<void> {
    try {
      // Get job card with work order operations
      const { data: jobCard, error } = await supabase
        .from('job_cards')
        .select(`
          id,
          work_order_id,
          work_order_operation_id,
          work_orders!inner(
            id,
            routing_id,
            routings!inner(
              routing_operations(
                id,
                sequence_number,
                operation_name,
                workstation_id,
                is_subcontracted
              )
            )
          )
        `)
        .eq('id', jobCardId)
        .single();

      if (error || !jobCard) {
        console.warn(`Job card ${jobCardId} not found for next operation scheduling:`, error);
        return;
      }

      // Find next in-house operations
      const allOperations = jobCard.work_orders.routings.routing_operations || [];
      const currentOperation = allOperations.find(op => op.id === jobCard.work_order_operation_id);
      
      if (!currentOperation) return;

      const nextOperations = allOperations
        .filter(op => 
          op.sequence_number > currentOperation.sequence_number && 
          !op.is_subcontracted
        )
        .sort((a, b) => a.sequence_number - b.sequence_number)
        .slice(0, 3); // Next 3 operations

      // Create schedule entries for next operations
      if (nextOperations.length > 0) {
        await this.scheduleNextOperations(jobCardId, nextOperations);
        console.log(`✅ Scheduled ${nextOperations.length} next operations for job card ${jobCardId}`);
      }

    } catch (error) {
      console.error('Error in checkAndScheduleNextOperations:', error);
      // Don't throw - this is enhancement, not critical
    }
  }

  /**
   * Schedule next operations for a job card
   */
  private async scheduleNextOperations(jobCardId: string, operations: any[]): Promise<void> {
    try {
      const scheduleEntries = operations.map((op, index) => {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + index); // Stagger by days
        
        const endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + 8); // Assume 8 hours per operation

        return {
          workstation_id: op.workstation_id,
          job_card_id: jobCardId,
          operation_id: op.id,
          scheduled_start: startDate.toISOString(),
          scheduled_end: endDate.toISOString(),
          status: 'scheduled' as const,
          notes: `Auto-scheduled after subcontract completion`,
          created_at: new Date().toISOString()
        };
      });

      const { error } = await supabase
        .from('workstation_schedules')
        .insert(scheduleEntries);

      if (error) {
        console.error('Error creating next operation schedules:', error);
        // Don't throw - this is enhancement, not critical
      }
    } catch (error) {
      console.error('Error in scheduleNextOperations:', error);
      // Don't throw - this is enhancement, not critical
    }
  }

  /**
   * Archive old schedule entries for a subcontract
   */
  private async archiveSubcontractSchedules(subContractId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('workstation_schedules')
        .update({
          status: 'completed',
          notes: `Archived - subcontract ${subContractId} completed`,
          updated_at: new Date().toISOString()
        })
        .eq('status', 'outsourced')
        .like('notes', `%${subContractId}%`);

      if (error) {
        console.error('Error archiving subcontract schedules:', error);
        // Don't throw - this is cleanup, not critical
      } else {
        console.log(`✅ Archived schedules for subcontract ${subContractId}`);
      }
    } catch (error) {
      console.error('Error in archiveSubcontractSchedules:', error);
      // Don't throw - this is cleanup, not critical
    }
  }

  /**
   * Get workstation capacity summary affected by subcontracting
   */
  async getWorkstationCapacitySummary(workstationIds: string[], dateFrom: string, dateTo: string): Promise<Array<{
    workstationId: string;
    workstationName: string;
    totalFreedHours: number;
    adjustmentCount: number;
    subContractCount: number;
  }>> {
    try {
      const { data, error } = await supabase
        .from('workstation_capacity_logs')
        .select(`
          workstation_id,
          adjustment_hours,
          workstations!inner(name)
        `)
        .in('workstation_id', workstationIds)
        .eq('adjustment_type', 'capacity_freed')
        .gte('adjustment_date', dateFrom)
        .lte('adjustment_date', dateTo)
        .eq('reference_type', 'sub_contract');

      if (error) throw error;

      // Group by workstation
      const summary = new Map();
      data?.forEach(log => {
        const wsId = log.workstation_id;
        if (!summary.has(wsId)) {
          summary.set(wsId, {
            workstationId: wsId,
            workstationName: log.workstations.name,
            totalFreedHours: 0,
            adjustmentCount: 0,
            subContractCount: 0
          });
        }
        
        const entry = summary.get(wsId);
        entry.totalFreedHours += log.adjustment_hours;
        entry.adjustmentCount += 1;
        entry.subContractCount += 1;
      });

      return Array.from(summary.values());
    } catch (error) {
      console.error('Error getting workstation capacity summary:', error);
      return [];
    }
  }
}

// Global scheduling integration instance
export const schedulingIntegration = new SchedulingIntegration();

// Register event listeners
subcontractingEvents.on('subcon.dispatched', (event) => {
  return schedulingIntegration.handleSubcontractDispatch(event);
});

subcontractingEvents.on('subcon.received', (event) => {
  return schedulingIntegration.handleSubcontractReceipt(event);
});

subcontractingEvents.on('subcon.closed', (event) => {
  return schedulingIntegration.handleSubcontractClosure(event);
});

export default schedulingIntegration;
